// src/lib/api.ts

// ─────────────────────────────────────────────
// Tipos base
// ─────────────────────────────────────────────
type ApiErrorPayload =
  | { error?: string; message?: string; details?: any }
  | string
  | null;

export type SourceMode = "creator" | "brand";

// ─────────────────────────────────────────────
// Base URL
// ─────────────────────────────────────────────
const rawBase = (process.env.REACT_APP_API_URL || "").trim();

// ✅ Normalizamos (sin slash final)
export const API_BASE_URL = rawBase.replace(/\/+$/, "");

/**
 * Construye URL y falla duro si falta REACT_APP_API_URL
 * (Evita que el frontend pegue por accidente a /api del mismo dominio)
 */
function buildUrl(path: string) {
  if (!API_BASE_URL) {
    throw new Error(
      "Missing REACT_APP_API_URL. Set it in your CRA .env / hosting env vars and rebuild/redeploy."
    );
  }
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE_URL}${cleanPath}`;
}

// ─────────────────────────────────────────────
// Helpers HTTP
// ─────────────────────────────────────────────
async function safeParseJson(resp: Response) {
  const text = await resp.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractErrorMessage(payload: ApiErrorPayload) {
  if (!payload) return "Unknown error";
  if (typeof payload === "string") return payload.slice(0, 300);
  return payload.error || payload.message || "Request failed";
}

// ─────────────────────────────────────────────
// Error tipado
// ─────────────────────────────────────────────
export class ApiError extends Error {
  status: number;
  url: string;
  payload: ApiErrorPayload;

  constructor(args: { status: number; url: string; message: string; payload: ApiErrorPayload }) {
    super(args.message);
    this.name = "ApiError";
    this.status = args.status;
    this.url = args.url;
    this.payload = args.payload;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return (
    !!err &&
    typeof err === "object" &&
    (err as any).name === "ApiError" &&
    typeof (err as any).status === "number"
  );
}

// ─────────────────────────────────────────────
// Fetch base
// ─────────────────────────────────────────────
export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = buildUrl(path);

  const headers = new Headers(options.headers || {});
  headers.set("Accept", "application/json");

  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const resp = await fetch(url, {
    ...options,
    headers,
  });

  const payload = (await safeParseJson(resp)) as any;

  if (!resp.ok) {
    const msg = extractErrorMessage(payload);

    // Detectar HTML (proxy mal configurado, 500 de Express, etc)
    if (typeof payload === "string" && payload.toLowerCase().includes("<!doctype")) {
      throw new ApiError({
        status: resp.status,
        url,
        message: `[${resp.status}] Non-JSON HTML response. Check API_BASE_URL / proxy. (${url})`,
        payload,
      });
    }

    throw new ApiError({
      status: resp.status,
      url,
      message: `[${resp.status}] ${msg}`,
      payload,
    });
  }

  if (typeof payload === "string") {
    throw new ApiError({
      status: resp.status,
      url,
      message: `Non-JSON response from API: ${payload.slice(0, 200)}`,
      payload,
    });
  }

  return payload as T;
}

// ─────────────────────────────────────────────
// Tipos estándar backend
// ─────────────────────────────────────────────
export type ApiOk<T extends object = {}> = { success: true } & T;
export type ApiFail = { success: false; error?: string };
export type ApiResponse<T extends object = {}> = ApiOk<T> | ApiFail;

export type MeResponse = ApiResponse<{
  user?: any;
  plan?: string;
  credits?: number;
}>;

// ─────────────────────────────────────────────
// Projects
// ─────────────────────────────────────────────
export type ProjectItem = {
  id: string;
  name: string;
  created_at: string;
  source_mode?: SourceMode | null;
};

export type ProjectsResponse = ApiResponse<{
  projects: Array<ProjectItem>;
}>;

export type CreateProjectResponse = ApiResponse<{
  project: ProjectItem;
}>;

// ─────────────────────────────────────────────
// Generations
// ─────────────────────────────────────────────
export type GenerationsListResponse = ApiResponse<{
  items: Array<{
    id: string;
    project_id: string | null;
    campaign_id?: string | null;
    prompt: string | null;
    style: string | null;
    aspect_ratio?: string | null;
    resolution?: string | null;
    engine?: string | null;
    cost_credits?: number | null;
    storage_path?: string | null;
    created_at: string;
    imageUrl?: string;
    source_mode?: SourceMode | null;
  }>;
}>;

export type GenerateResponse = ApiResponse<{
  imageUrl: string;
  generationId?: string;
  newBalance?: number;
  resolution?: string;
  engine?: string | null;
  costCredits?: number | null;
}>;

// ─────────────────────────────────────────────
// Packs / Stripe
// ─────────────────────────────────────────────
export type PacksResponse = ApiResponse<{
  packs: Array<{
    packId: string;
    credits: number;
    priceUsd: number;
    name?: string | null;
    badge?: string | null;
    description?: string | null;
  }>;
}>;

export type StripeCheckoutResponse = ApiResponse<{
  url: string;
}>;

// ─────────────────────────────────────────────
// Utils
// ─────────────────────────────────────────────
function buildQs(params?: Record<string, string | null | undefined>) {
  const qs = new URLSearchParams();
  if (!params) return "";
  for (const [k, v] of Object.entries(params)) {
    if (v !== null && typeof v !== "undefined" && String(v).trim() !== "") {
      qs.set(k, String(v));
    }
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

// ─────────────────────────────────────────────
// API pública
// ─────────────────────────────────────────────
export const api = {
  // ─── ME ─────────────────────────────
  me: (options?: RequestInit) =>
    apiFetch<MeResponse>("/api/me", options),

  // ─── PROJECTS ───────────────────────
  /**
   * ⚠️ Legacy (sin filtro). Evitar usarlo si no es necesario.
   */
  projects: (options?: RequestInit) =>
    apiFetch<ProjectsResponse>("/api/projects", {
      method: "GET",
      ...(options || {}),
    }),

  /**
   * ✅ Recomendado: Projects con separación Creator / Brand
   */
  projectsList: (
    params?: { limit?: number; sourceMode?: SourceMode },
    options?: RequestInit
  ) => {
    const qs = buildQs({
      limit: typeof params?.limit === "number" ? String(params.limit) : undefined,
      sourceMode: params?.sourceMode,
    });
    return apiFetch<ProjectsResponse>(`/api/projects${qs}`, {
      method: "GET",
      ...(options || {}),
    });
  },

  /**
   * Crear proyecto (si no mandas sourceMode → creator por defecto)
   */
  createProject: (
    body: { name: string; sourceMode?: SourceMode },
    options?: RequestInit
  ) =>
    apiFetch<CreateProjectResponse>("/api/projects", {
      method: "POST",
      body: JSON.stringify(body),
      ...(options || {}),
    }),

  // ─── GENERATIONS ────────────────────
  generationsList: (
    params?: { projectId?: string | null; sourceMode?: SourceMode },
    options?: RequestInit
  ) => {
    const qs = buildQs({
      projectId: params?.projectId || undefined,
      sourceMode: params?.sourceMode,
    });
    return apiFetch<GenerationsListResponse>(`/api/generations${qs}`, {
      method: "GET",
      ...(options || {}),
    });
  },

  generateLegacy: (body: any, options?: RequestInit) =>
    apiFetch<ApiResponse<any>>("/api/generate", {
      method: "POST",
      body: JSON.stringify(body),
      ...(options || {}),
    }),

  generate: (
    body: {
      prompt: string;
      style: string;
      aspectRatio: string;
      resolution: string;
      projectId?: string | null;
      campaignId?: string | null;
      sourceMode?: SourceMode;
    },
    options?: RequestInit
  ) =>
    apiFetch<GenerateResponse>("/api/generations/generate", {
      method: "POST",
      body: JSON.stringify(body),
      ...(options || {}),
    }),

  // ─── PACKS / STRIPE ─────────────────
  packs: (options?: RequestInit) =>
    apiFetch<PacksResponse>("/api/packs", {
      method: "GET",
      ...(options || {}),
    }),

  stripeCheckoutPack: (
    body: { packKey: "XS" | "S" | "M" | "L" | "XL" },
    options?: RequestInit
  ) =>
    apiFetch<StripeCheckoutResponse>("/api/stripe/checkout-pack", {
      method: "POST",
      body: JSON.stringify(body),
      ...(options || {}),
    }),
};


